import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiBriefcase, FiCheckCircle, FiGlobe, FiShield } from 'react-icons/fi';
import { useLanguage } from '../components';
import ComplianceNotice from '../components/ComplianceNotice';
import { PLANNING_OUTCOMES, localizeContent } from '../content/planningContent';
import './Landing.css';

const CONTENT = {
  en: {
    title: 'Understand and plan your financial future in Canada',
    subtitle:
      'Learn Canadian accounts, set practical goals, forecast growth, and explore retirement scenarios with educational tools built for everyday decisions.',
    primaryCta: 'Start with planning',
    secondaryCta: 'Open advanced portfolio tools',
    tertiaryCta: 'Sign in',
    question: 'What can this help me accomplish?',
    recommendation: 'Recommended',
    advancedTitle: 'Already ready for portfolio tools?',
    advancedText:
      'Portfolio optimization, analytics, asset research, and comparison tools remain available as an advanced module.',
    trustTitle: 'Built for clarity, not advice',
    trustPoints: [
      'Canadian account context for TFSA, RRSP, FHSA, RESP, and RRIF.',
      'Scenario modeling with visible assumptions and uncertainty.',
      'Education-first wording in English and French.',
    ],
    language: 'Language',
    switchLanguage: 'Passer au francais',
    switchShort: 'FR',
    signInHint: 'Already have an account?',
    disclosureLink: 'Read methodology and disclosures',
    footerText: 'Hamilton Services | Canadian financial education and scenario modeling',
  },
  fr: {
    title: 'Comprenez et planifiez votre avenir financier au Canada',
    subtitle:
      'Apprenez les comptes canadiens, définissez des objectifs, projetez la croissance et explorez la retraite avec des outils éducatifs.',
    primaryCta: 'Commencer par la planification',
    secondaryCta: 'Ouvrir les outils avancés',
    tertiaryCta: 'Se connecter',
    question: 'Que voulez-vous accomplir ?',
    recommendation: 'Recommandé',
    advancedTitle: 'Prêt pour les outils de portefeuille ?',
    advancedText:
      'L’optimisation de portefeuille, les analyses, la recherche d’actifs et les comparaisons restent disponibles comme module avancé.',
    trustTitle: 'Conçu pour clarifier, pas conseiller',
    trustPoints: [
      'Contexte canadien pour CELI, REER, CELIAPP, REEE et FERR.',
      'Modélisation de scénarios avec hypothèses et incertitude visibles.',
      'Approche éducative bilingue en anglais et en français.',
    ],
    language: 'Langue',
    switchLanguage: 'Switch to English',
    switchShort: 'EN',
    signInHint: 'Vous avez déjà un compte ?',
    disclosureLink: 'Lire la méthodologie et les avis',
    footerText: 'Hamilton Services | Education financiere canadienne et modelisation de scenarios',
  },
};

const Landing = ({ onStartPlanning, onOpenOptimization, onLogin }) => {
  const { lang, setLang } = useLanguage();
  const c = CONTENT[lang] || CONTENT.en;

  return (
    <div className="landing-root">
      <button
        type="button"
        className="landing-lang-toggle"
        onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
        title={c.switchLanguage}
        aria-label={c.switchLanguage}
      >
        <FiGlobe aria-hidden="true" />
        <span>{c.language}</span>
        <strong>{c.switchShort}</strong>
      </button>

      <main className="landing-stage">
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <h1 className="landing-title">{c.title}</h1>
            <p className="landing-subtitle">{c.subtitle}</p>
            <div className="landing-cta-row">
              <button className="landing-btn primary" onClick={() => onStartPlanning('/planning')}>
                {c.primaryCta}
              </button>
              <button className="landing-btn secondary" onClick={onOpenOptimization}>
                {c.secondaryCta}
              </button>
            </div>
            <button className="landing-inline-auth" onClick={onLogin}>
              <span>{c.signInHint}</span>
              <strong>{c.tertiaryCta}</strong>
            </button>
          </div>

          <div className="landing-choice-panel" aria-label={c.question}>
            <h2>{c.question}</h2>
            <div className="landing-outcome-list">
              {PLANNING_OUTCOMES.map((item, index) => {
                const Icon = item.icon;
                const copy = localizeContent(item, lang);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="landing-outcome-row"
                    onClick={() => onStartPlanning(item.path)}
                  >
                    <span className="landing-outcome-icon"><Icon /></span>
                    <span className="landing-outcome-copy">
                      <strong>
                        {copy.title}
                        {index === 0 && <em><FiCheckCircle /> {c.recommendation}</em>}
                      </strong>
                      <small>{copy.description}</small>
                    </span>
                    <FiArrowRight className="landing-outcome-arrow" />
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <ComplianceNotice className="landing-compliance" />

        <section className="landing-secondary-grid">
          <article className="landing-advanced-panel">
            <div className="landing-feature-icon"><FiBriefcase size={22} /></div>
            <div>
              <h2>{c.advancedTitle}</h2>
              <p>{c.advancedText}</p>
            </div>
            <button className="landing-text-action" onClick={onOpenOptimization}>
              {c.secondaryCta} <FiArrowRight />
            </button>
          </article>

          <article className="landing-trust">
            <div className="landing-feature-icon"><FiShield size={22} /></div>
            <div>
              <h2>{c.trustTitle}</h2>
              <ul>
                {c.trustPoints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <Link to="/help" className="landing-inline-link">{c.disclosureLink}</Link>
            </div>
          </article>
        </section>
      </main>

      <footer className="landing-footer">
        <p>{c.footerText}</p>
      </footer>
    </div>
  );
};

export default Landing;
